import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import { GroupRide } from '../../types';
import GroupRideRouteDisplay from './GroupRideRouteDisplay';
import RideSlideshowModal from '../feed/RideSlideshowModal';
import './GroupRideDetailView.css';

interface GroupRideDetailViewProps {
  rideId: number;
  onBack: () => void;
  isLoaded: boolean;
}

const GroupRideDetailView: React.FC<GroupRideDetailViewProps> = ({ rideId, onBack, isLoaded }) => {
  const { t } = useLanguage();
  const [ride, setRide] = useState<GroupRide | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSlideshow, setShowSlideshow] = useState(false);

  // Email invite
  const [showEmailInvite, setShowEmailInvite] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteResult, setInviteResult] = useState<string | null>(null);

  const handleEmailInvite = async () => {
    const emails = inviteEmails.split(/[,;\n]+/).map(e => e.trim()).filter(e => e);
    if (!emails.length) return;
    setInviteSending(true);
    setInviteResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/group-rides/${rideId}/invite-email`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails })
      });
      const data = await res.json();
      if (res.ok) {
        const parts = [];
        if (data.sent?.length) parts.push(`Sent to ${data.sent.length}`);
        if (data.failed?.length) parts.push(`Failed: ${data.failed.join(', ')}`);
        setInviteResult(parts.join('. '));
        if (data.sent?.length) setInviteEmails('');
      } else {
        setInviteResult(data.error || 'Failed to send');
      }
    } catch {
      setInviteResult('Failed to send invitations');
    } finally {
      setInviteSending(false);
    }
  };

  useEffect(() => {
    const fetchRide = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/api/group-rides/${rideId}`, {
          credentials: 'include',
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        setRide(data);
      } catch (err: any) {
        console.error('Error fetching group ride:', err);
        setError(err.message || 'Failed to load ride');
      } finally {
        setLoading(false);
      }
    };
    fetchRide();
  }, [rideId]);

  if (loading) {
    return (
      <div className="group-ride-detail">
        <div className="group-ride-detail-loading">{t('common.loading') || 'Loading...'}</div>
      </div>
    );
  }

  if (error || !ride) {
    return (
      <div className="group-ride-detail">
        <div className="group-ride-detail-header">
          <button className="group-ride-detail-back" onClick={onBack}>
            &#x2190; {t('common.back') || 'Back'}
          </button>
        </div>
        <div className="group-ride-detail-error">{error || 'Ride not found'}</div>
      </div>
    );
  }

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="group-ride-detail">
      <div className="group-ride-detail-header">
        <button className="group-ride-detail-back" onClick={onBack}>
          &#x2190; {t('common.back') || 'Back'}
        </button>
        <h2 className="group-ride-detail-title">{ride.title}</h2>
      </div>

      <div className="group-ride-detail-info">
        <div className="group-ride-detail-meta">
          <span className="group-ride-detail-date">{formatDate(ride.ride_date)}</span>
          {ride.start_time && (
            <span className="group-ride-detail-time">{ride.start_time}</span>
          )}
        </div>

        {ride.start_location_name && (
          <div className="group-ride-detail-location">
            <span className="group-ride-detail-location-label">{t('groupRide.meetingPoint') || 'Meeting point'}:</span>
            <span>{ride.start_location_name}</span>
          </div>
        )}

        {ride.description && (
          <p className="group-ride-detail-description">{ride.description}</p>
        )}

        <div className="group-ride-detail-participants">
          {ride.participant_count} {t('groupRide.participants') || 'participants'}
          {ride.max_participants && ` / ${ride.max_participants}`}
        </div>

        {ride.status !== 'completed' && ride.status !== 'cancelled' && (
          <div className="group-ride-email-invite" style={{ marginTop: '16px' }}>
            <button
              className="btn btn-secondary"
              onClick={() => setShowEmailInvite(!showEmailInvite)}
              style={{ fontSize: '0.85rem' }}
            >
              {showEmailInvite ? 'Hide' : 'Invite by email'}
            </button>
            {showEmailInvite && (
              <div style={{ marginTop: '10px' }}>
                <textarea
                  value={inviteEmails}
                  onChange={(e) => setInviteEmails(e.target.value)}
                  placeholder="Enter emails (comma or newline separated)"
                  rows={3}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.9rem', resize: 'vertical' }}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleEmailInvite}
                  disabled={inviteSending || !inviteEmails.trim()}
                  style={{ marginTop: '8px', fontSize: '0.85rem' }}
                >
                  {inviteSending ? 'Sending...' : 'Send invitations'}
                </button>
                {inviteResult && (
                  <div style={{ marginTop: '8px', fontSize: '0.85rem', color: inviteResult.includes('Failed') ? '#c00' : '#333' }}>
                    {inviteResult}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {ride.status === 'completed' && (
          <button
            className="group-ride-detail-slideshow-btn"
            onClick={() => setShowSlideshow(true)}
          >
            Ride Slideshow
          </button>
        )}
      </div>

      <GroupRideRouteDisplay
        rideId={ride.id}
        routeSource={ride.route_source}
        routeDistance={ride.route_distance}
        routeElevationGain={ride.route_elevation_gain}
        routePolyline={ride.route_polyline}
        stravaRouteUrl={ride.strava_route_url}
        canDownloadGpx={ride.can_download_gpx}
        isLoaded={isLoaded}
        endLocationName={ride.end_location_name}
        endLocationLat={ride.end_location_lat}
        endLocationLng={ride.end_location_lng}
      />

      {showSlideshow && (
        <RideSlideshowModal rideId={ride.id} onClose={() => setShowSlideshow(false)} />
      )}
    </div>
  );
};

export default GroupRideDetailView;
